import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { ReactTable } from "components";
import { useDealerItemsQuery } from "modules/DealerLicenseOverview/hooks";
import { combinedLicenseData } from "modules/LocationLicenseOverview/utils";
import { formatPrice } from "utils/formatPrice";

import type { LicenseType } from "modules/LocationLicenseOverview";
import type { Licenses } from "modules/LocationLicenseOverview/components";

type MonthlyTableProps = {
  isLoadingDealerLicenses: boolean;
  licenses?: Licenses;
};

const columnHelper = createColumnHelper<LicenseType>();

export const MonthlyTable = ({ isLoadingDealerLicenses, licenses }: MonthlyTableProps) => {
  const { t } = useTranslation();

  const { dealerItems, isLoadingDealerItems } = useDealerItemsQuery();
  const monthlyData = useMemo(() => combinedLicenseData(licenses, dealerItems, t, true), [licenses, dealerItems]);

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "code",
        header: t("v8_code", "Code"),
        cell: ({ row: { original } }) =>
          original.code ? (
            <div className="license-code">{original.code}</div>
          ) : (
            <div className="no-license-code">
              <Icon className="link slash solid red" />
            </div>
          ),
        size: 50
      }),
      columnHelper.accessor(row => row, {
        id: "location_name",
        header: t("v8_location", "Location"),
        cell: ({ row: { original } }) => original.dealer_location_name || "",
        size: 100
      }),
      columnHelper.accessor(row => row, {
        id: "name",
        header: t("v8_name", "Name"),
        cell: ({ row: { original } }) => <span>{original.description || original.name || ""}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "category",
        header: t("v8_role_license_service", "Role / License / Service"),
        cell: ({ row: { original } }) => original.category,
        size: 300
      }),
      columnHelper.accessor(row => row, {
        id: "quantity",
        header: t("v8_amount", "Amount"),
        cell: ({ row: { original } }) => original.quantity,
        size: 100
      }),
      columnHelper.accessor(row => row, {
        id: "total_price",
        header: t("v8_total_price", "Total Price"),
        cell: ({ row: { original } }) => `€ ${formatPrice(original.totalPrice)}`,
        size: 100
      })
    ],
    [t]
  );

  return (
    <div className="MonthlyTable mb-25">
      <ReactTable
        isFetching={isLoadingDealerLicenses || isLoadingDealerItems}
        columns={columns}
        data={monthlyData}
        emptyRowMessage={t("v8_no_monthly_licenses", "No monthly licenses")}
      />
    </div>
  );
};
